import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Phone, ShieldCheck, UserPlus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Ok, Panel, Tag } from "@/components/community/kit";
import { ROLE_TONE, allows, day } from "@/lib/community";

export const Members = ({ community, onChanged }) => {
  const cid = community.id;
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [invite, setInvite] = useState({ email: "", role: "member" });
  const [profile, setProfile] = useState(null);
  const manage = allows(community, "members", "full") || community.platform_admin;
  const canInvite = allows(community, "members", "limited") || community.platform_admin;
  const canGrant = allows(community, "settings", "full") || community.platform_admin;

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/members`);
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data || profile || community.platform_admin) return;
    const me = data.members.find((m) => m.user_id === String(user?.id || ""));
    setProfile({
      display_name: me?.name || "", driving_years: me?.driving_years || 0,
      bio: me?.bio || "", city: me?.city || "", phone: me?.phone || "",
      show_contact: Boolean(me?.show_contact),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const act = async (memberId, payload, key) => {
    setBusy(key); setError(""); setNotice("");
    try {
      await api.post(`/communities/${cid}/members/${memberId}`, payload);
      await load();
      onChanged?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const sendInvite = async () => {
    setBusy("invite"); setError(""); setNotice("");
    try {
      await api.post(`/communities/${cid}/invite`, invite);
      setNotice(`Invitation emailed to ${invite.email} with the community code.`);
      setInvite({ email: "", role: "member" });
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const saveProfile = async () => {
    setBusy("profile"); setError(""); setNotice("");
    try {
      await api.patch(`/communities/${cid}/me`, profile);
      setNotice("Your community profile is saved.");
      await load();
      onChanged?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  const pending = data.members.filter((m) => m.status === "pending");
  const active = data.members.filter((m) => m.status === "active");
  const suspended = data.members.filter((m) => m.status === "suspended");

  return (
    <div data-testid="community-members" className="space-y-4">
      <Err testId="members-error">{error}</Err>
      <Ok testId="members-notice">{notice}</Ok>

      {pending.length > 0 && (
        <Panel testId="members-pending" title={`Waiting to be let in · ${pending.length}`}>
          <ul className="divide-y divide-ink-600">
            {pending.map((m) => (
              <li key={m.id} data-testid={`pending-row-${m.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-100">{m.name}</p>
                  <p className="truncate text-xs text-slate-500">{m.email} · {m.account_role}</p>
                </div>
                {manage && (
                  <div className="flex gap-2">
                    <Button data-testid={`member-approve-${m.id}`} size="sm"
                      onClick={() => act(m.id, { action: "approve" }, `approve-${m.id}`)}
                      disabled={busy === `approve-${m.id}`}>
                      Let them in
                    </Button>
                    <Button data-testid={`member-decline-${m.id}`} size="sm" variant="outline"
                      onClick={() => act(m.id, { action: "decline" }, `decline-${m.id}`)}
                      disabled={busy === `decline-${m.id}`}>
                      Decline
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel testId="members-roster" title={`Members · ${active.length}`}
        sub="Phone numbers show only for members who chose to share them.">
        {active.length === 0 ? (
          <Empty testId="members-empty">Nobody has joined yet.</Empty>
        ) : (
          <ul className="divide-y divide-ink-600">
            {active.map((m) => (
              <li key={m.id} data-testid={`member-row-${m.id}`} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm text-slate-100">{m.name}</p>
                      <Chip className={ROLE_TONE[m.role]}>{m.role_label}</Chip>
                      {(m.positions || []).map((p) => (
                        <Chip key={p} className="border-gold/30 bg-gold/5 text-gold/90">{p}</Chip>
                      ))}
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      {m.city && <span>{m.city}</span>}
                      {m.driving_years > 0 && <span>{m.driving_years} yrs driving</span>}
                      {m.phone && (
                        <a href={`tel:${m.phone}`} data-testid={`member-phone-${m.id}`}
                          className="flex items-center gap-1 text-slate-400 hover:text-gold">
                          <Phone className="h-3 w-3" /> {m.phone}
                        </a>
                      )}
                      {m.email && (
                        <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-gold">
                          <Mail className="h-3 w-3" /> {m.email}
                        </a>
                      )}
                      {m.dues_paid_through && <span>dues to {day(m.dues_paid_through)}</span>}
                      {m.joined_at && <span>joined {day(m.joined_at)}</span>}
                    </p>
                    {m.bio && <p className="mt-1.5 text-xs text-slate-400">{m.bio}</p>}
                  </div>
                  {manage && (
                    <div className="flex flex-wrap items-center gap-2">
                      {canGrant && (
                        <select data-testid={`member-role-${m.id}`} value={m.role}
                          onChange={(e) => act(m.id, { action: "role", role: e.target.value }, `role-${m.id}`)}
                          className="h-9 rounded-md border border-ink-600 bg-ink-900 px-2 text-xs text-slate-200">
                          {data.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </select>
                      )}
                      <Button data-testid={`member-suspend-${m.id}`} size="sm" variant="outline"
                        onClick={() => act(m.id, { action: "suspend", reason: "" }, `suspend-${m.id}`)}
                        disabled={busy === `suspend-${m.id}`}>
                        Suspend
                      </Button>
                      <Button data-testid={`member-remove-${m.id}`} size="sm" variant="ghost"
                        className="text-slate-400 hover:text-red-300"
                        onClick={() => act(m.id, { action: "remove" }, `remove-${m.id}`)}
                        disabled={busy === `remove-${m.id}`}>
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {suspended.length > 0 && manage && (
        <Panel testId="members-suspended" title={`Suspended · ${suspended.length}`}>
          <ul className="divide-y divide-ink-600">
            {suspended.map((m) => (
              <li key={m.id} data-testid={`suspended-row-${m.id}`}
                className="flex items-center justify-between gap-3 py-3">
                <p className="truncate text-sm text-slate-300">{m.name}</p>
                <Button data-testid={`member-reinstate-${m.id}`} size="sm" variant="outline"
                  onClick={() => act(m.id, { action: "reinstate" }, `reinstate-${m.id}`)}>
                  Reinstate
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {canInvite && (
        <Panel testId="members-invite" title="Invite someone"
          sub="They get an email with the code. Invited members skip the approval queue.">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input data-testid="invite-email" type="email" placeholder="driver@example.com"
              value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            {canGrant && (
              <select data-testid="invite-role" value={invite.role}
                onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                <option value="member">Member</option>
                <option value="moderator">Moderator</option>
                <option value="treasurer">Treasurer</option>
                <option value="leader">Leader</option>
              </select>
            )}
            <Button data-testid="invite-send" onClick={sendInvite}
              disabled={!invite.email.includes("@") || busy === "invite"}>
              {busy === "invite" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <UserPlus className="mr-2 h-4 w-4" />}
              Invite
            </Button>
          </div>
        </Panel>
      )}

      {profile && (
        <Panel testId="members-profile" title="How you appear here"
          sub="This is separate from your operator profile — the community sees only this.">
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="profile-name" placeholder="Name members see"
                value={profile.display_name}
                onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} />
              <Input data-testid="profile-city" placeholder="City you work"
                value={profile.city}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })} />
              <Input data-testid="profile-phone" placeholder="Phone" value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              <Input data-testid="profile-years" type="number" min="0" max="70"
                placeholder="Years driving" value={profile.driving_years}
                onChange={(e) => setProfile({ ...profile, driving_years: Number(e.target.value) })} />
            </div>
            <Textarea data-testid="profile-bio" rows={3} placeholder="A line about you"
              value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input data-testid="profile-show-contact" type="checkbox" checked={profile.show_contact}
                onChange={(e) => setProfile({ ...profile, show_contact: e.target.checked })}
                className="h-4 w-4" />
              Show my phone number to other members
            </label>
            <Button data-testid="profile-save" onClick={saveProfile} disabled={busy === "profile"}>
              {busy === "profile" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <ShieldCheck className="mr-2 h-4 w-4" />}
              Save
            </Button>
          </div>
        </Panel>
      )}

      <Tag>{data.counts?.members || 0} active · {data.counts?.pending || 0} waiting</Tag>
    </div>
  );
};
